import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Prescription } from "@/types";
import { useNavigate } from "react-router-dom";
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { FileText, Printer } from "lucide-react";
import { useState } from "react";
import { PrescriptionViewerDialog } from "../prescriptions/PrescriptionViewerDialog";
import { useQueueStore } from "@/lib/store";
import { formatPatientId } from '@/lib/utils/patientId';
import { format } from "date-fns";

interface PatientPrescriptionHistoryProps {
  prescriptions: Prescription[];
  patientId: string;
}

export function PatientPrescriptionHistory({ 
  prescriptions, 
  patientId 
}: PatientPrescriptionHistoryProps) {
  const navigate = useNavigate();
  const { patients, doctors } = useQueueStore();
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const { profile } = useHospitalProfileStore();
  
  const patient = patients.find(p => p.id === patientId);
  
  const getDoctorName = (doctorId: string) => {
    const foundDoctor = doctors.find(d => d.id === doctorId);
    return foundDoctor ? foundDoctor.name : "Unknown Doctor";
  };
  
  const handlePrint = () => {
    if (!selectedPrescription) return;
    
    const printContent = document.getElementById("prescription-print-content");
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };
  
  const handleViewPrescription = (prescription: Prescription) => {
    if (prescription.status === 'draft') {
      // For drafts, navigate to E-prescription page for editing
      navigate(`/e-prescription/${patientId}`);
    } else {
      // For issued prescriptions, open viewer dialog
      setSelectedPrescription(prescription);
    }
  };

  /** Opens the full prescription preview (correct footer/layout) and triggers browser print -> Save as PDF */
  const openPrintPreview = (prescription: Prescription) => {
    handleViewPrescription(prescription);
    setTimeout(handlePrint, 500);
  };
  
  const printPrescription = (prescription: Prescription) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doctor = doctors.find(d => d.id === prescription.doctorId);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Prescription - ${patient?.name}</title>
          <style>
            @media print {
              @page {
                size: A4;
                margin: 0.5in;
              }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.6;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            .clinic-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .clinic-details {
              font-size: 11px;
              color: #666;
              margin-bottom: 5px;
            }
            .prescription-date {
              text-align: right;
              margin-bottom: 20px;
            }
            .patient-info {
              margin-bottom: 20px;
            }
            .medication {
              margin-bottom: 20px;
            }
            .footer {
              margin-top: 40px;
              text-align: right;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="clinic-name">${profile.name}</div>
            <div class="clinic-details">${profile.description}</div>
            <div class="clinic-details">${profile.address}</div>
            <div class="clinic-details">Phone: ${profile.phone1} | Email: ${profile.email}</div>
            ${profile.website ? `<div class="clinic-details">Website: ${profile.website}</div>` : ''}
          </div>

          <div class="prescription-date">
            Date: ${format(new Date(), "dd MMM yyyy")}
          </div>

          <div class="patient-info">
            <div><strong>Patient Name:</strong> ${patient?.name}</div>
            <div><strong>Age/Gender:</strong> ${patient?.age} / ${patient?.gender}</div>
            <div><strong>Patient ID:</strong> ${patient?.id ? formatPatientId(patient.id) : 'N/A'}</div>
          </div>

          <div class="medication">
            <h3>Medication:</h3>
            ${prescription.medications.map(med => `
              <div>
                <strong>${med.name}</strong> - ${med.dosage} ${med.doseType ? med.doseType : ''}
                <div>${med.frequency} for ${med.duration}</div>
              </div>
            `).join('')}
          </div>

          ${prescription.notes ? `
            <div class="notes">
              <h3>Additional Notes:</h3>
              <div>${prescription.notes}</div>
            </div>
          ` : ''}

          ${prescription.follow_up_date ? `
            <div class="follow-up">
              <h3>Follow-up Date:</h3>
              <div>${format(new Date(prescription.follow_up_date), "dd MMM yyyy")}</div>
            </div>
          ` : ''}

          <div class="footer">
            <div><strong>Doctor:</strong> ${doctor?.name}</div>
            <div><strong>Registration No:</strong> ${doctor?.registrationNumber}</div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };
  
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Prescription History</CardTitle>
        </CardHeader>
        <CardContent>
          {prescriptions.length === 0 ? (
            <p className="text-center text-muted-foreground">No prescriptions found</p>
          ) : (
            <div className="space-y-4">
              {prescriptions.map((prescription) => (
                <div key={prescription.id} className="border rounded-md p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant={prescription.status === 'issued' ? 'default' : 'outline'}>
                          {prescription.status === 'issued' ? 'Issued' : 'Draft'}
                        </Badge>
                        <span className="text-sm font-medium">
                          {new Date(prescription.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-sm text-muted-foreground ml-2">
                          <span className="font-medium">Patient ID:</span> {patient?.id ? formatPatientId(patient.id) : 'N/A'}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Medications: {prescription.medications.length}
                      </p>
                      {prescription.diagnosis && (
                        <p className="mt-1 text-sm">
                          <span className="font-medium">Diagnosis:</span> {prescription.diagnosis}
                        </p>
                      )}
                      {prescription.chief_complaints && (
                        <p className="mt-1 text-sm">
                          <span className="font-medium">Complaints:</span> {prescription.chief_complaints}
                        </p>
                      )}
                      {prescription.lab_tests && prescription.lab_tests.length > 0 && (
                        <p className="mt-1 text-sm">
                          <span className="font-medium">Tests:</span> {prescription.lab_tests.length} test(s)
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewPrescription(prescription)}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {prescription.status === 'draft' ? 'Edit' : 'View'}
                      </Button>
                      {prescription.status === 'issued' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPrintPreview(prescription)}
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {patient && selectedPrescription && (
        <PrescriptionViewerDialog
          prescription={selectedPrescription}
          isOpen={!!selectedPrescription}
          onOpenChange={(open) => !open && setSelectedPrescription(null)}
          patientName={patient.name}
          patientAge={patient.age}
          patientGender={patient.gender}
          getDoctorName={getDoctorName}
          onPrint={handlePrint}
          doctors={doctors}
          patient={patient}
          patientContactNumber={patient.contactNumber}
        />
      )}
    </>
  );
}
